import React, { useEffect, useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { Add, Delete, Remove } from "@mui/icons-material";
import { TimeAdjustmentsStyled, StyledInfoBox } from "./reserve-data.styles";
import { getAllReserveData, updateTimeAdjustment } from "../../../../actions";
import { Button, LightButton } from "../../../../components/button/button";
import TextInputFormField from "../../../../components/form-fields/text-input-field/text-input-form-field";
import { useForm } from "react-hook-form";
import { addNotification } from "../../../../store/slices/ui-slice";
import { v4 as uuid } from "uuid";
import { useAppDispatch } from "../../../../store/hooks";
import Loader from "../../../../components/loader/loader";
import { yupResolver } from "@hookform/resolvers/yup";
import { ReservedDataValidation } from "../../../../utils/validations";
import { DeleteIcon } from "../../../../assets/svgComponents";

const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

interface FormData {
  [key: string]: string | number; // Adjust this to match the exact structure of your validation schema
}

const ReserveTimeAdjustments: React.FC = () => {
  const [timeAdjustmentData, setTimeAdjustmentData] = useState<any>({});
  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty },
  } = useForm<FormData>({
    resolver: yupResolver(ReservedDataValidation()),
    mode: "all",
  });

  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getAllReserveData();
        if (response.data.data.data) {
          setTimeAdjustmentData(response.data.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
        dispatch(
          addNotification({
            id: uuid(),
            type: "error",
            message: "Failed to fetch data",
          })
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dispatch]);

  const addNewEntry = (dayKey: string) => {
    setTimeAdjustmentData((prev: any) => ({
      ...prev,
      [dayKey]: [...(prev[dayKey] || []), { priority: 0, reserved: 0 }],
    }));
  };

  const removeLastEntry = (dayKey: string) => {
    setTimeAdjustmentData((prev: any) => {
      const updatedDayData = [...(prev[dayKey] || [])];
      updatedDayData.pop(); // Remove the last entry
      return {
        ...prev,
        [dayKey]: updatedDayData,
      };
    });
  };

  const onSubmit = async (data: FormData) => {
    const payload = Object.keys(data).reduce(
      (acc, dayKey) => {
        const [day, field, index] = dayKey.split("_");
        if (!acc[day]) acc[day] = [];
        if (!acc[day][Number(index)]) acc[day][Number(index)] = {};
        acc[day][Number(index)][field] = Number(data[dayKey]);

        return acc;
      },
      {} as Record<string, Array<Record<string, number>>>
    );
    setSubmitting(true);
    try {
      await updateTimeAdjustment(payload);
      dispatch(
        addNotification({
          id: uuid(),
          type: "success",
          message: "Time adjustment saved successfully!",
        })
      );
    } catch (error: any) {
      dispatch(
        addNotification({
          id: uuid(),
          type: "error",
          message:
            error.response?.data?.message || "Failed to save time adjustment",
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Loader absolute />;
  return (
    <TimeAdjustmentsStyled>
      <Box className="header">
        <Typography variant="h2">Reserve Data</Typography>
        <StyledInfoBox>
          <Typography variant="body2">
            Adjust the amount of reserved time that will be held until 48 hours
            out.
          </Typography>
          <Typography variant="body2">
            - A Priority value of 9, which is the highest, will reject all
            appointments and save the amount of time reserved.
          </Typography>
          <Typography variant="body2">
            - A Priority value of 8 will make sure we save the amount of time
            reserved for only Major Sick appointments.
          </Typography>
          <Typography variant="body2">
            - Priority 7 reserves time for Major and Minor Sick, 6 for Major and
            Minor Sick as well as Euth, etc.
          </Typography>
        </StyledInfoBox>
        <div className="time-card-wrapper">
          <Box className="card-wrapper">
            <div className="content-wrapper">
              {/* <Typography variant="h4">Reserved Time by Day</Typography> */}

              {/* <br /> */}
              <div className="fields-wrapper">
                {daysOfWeek.map((day, index) => {
                  const dayKey = index.toString();
                  const dayData = timeAdjustmentData[dayKey] || [];

                  return (
                    <div className="input-pair" key={dayKey}>
                      <Typography variant="h5">{day}</Typography>
                      {dayData.length > 0 ? (
                        <>
                          {dayData.map((entry: any, entryIndex: number) => (
                            <div
                              key={`${dayKey}_${entryIndex}`}
                              className="entry-row"
                            >
                              <TextInputFormField
                                name={`${dayKey}_priority_${entryIndex}`}
                                control={control}
                                label="Priority"
                                inputType="number"
                                defaultValue={entry.priority}
                                placeholder="Enter priority"
                              />
                              <TextInputFormField
                                name={`${dayKey}_reserved_${entryIndex}`}
                                control={control}
                                label="Reserved"
                                inputType="number"
                                defaultValue={entry.reserved}
                                placeholder="Enter reserved"
                              />
                              <IconButton
                                onClick={() => removeLastEntry(dayKey)}
                                color="error"
                                disableRipple
                                style={{ marginTop: "24px" }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </div>
                          ))}
                          <Box className="add-btn-wrapper">
                            <Button
                              variant="outlined"
                              startIcon={<Add />}
                              onClick={() => addNewEntry(dayKey)}
                            >
                              Add More
                            </Button>
                          </Box>
                        </>
                      ) : (
                        <div className="no-entry centered">
                          <Typography>No entries for this day.</Typography>
                          <Button
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={() => addNewEntry(dayKey)}
                          >
                            Add Entry
                          </Button>
                        </div>
                      )}
                      {/* <Box className="button-row">
                        <IconButton
                          onClick={() => addNewEntry(dayKey)}
                          color="primary"
                        >
                          <Add />
                        </IconButton>
                        <IconButton
                          onClick={() => removeLastEntry(dayKey)}
                          color="secondary"
                        >
                          <Remove />
                        </IconButton>
                      </Box> */}
                    </div>
                  );
                })}
              </div>
              <div className="button-wrapper">
                <Button
                  disabled={!isValid || !isDirty || submitting}
                  isLoading={submitting}
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleSubmit(onSubmit)}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </Box>
        </div>
      </Box>
    </TimeAdjustmentsStyled>
  );
};

export default ReserveTimeAdjustments;
